import React from 'react';

/**
 * Recovers from a stale app bundle after a deploy.
 *
 * Every route/component in this app is React.lazy()-loaded. A tab left open
 * across a deploy holds an old chunk manifest; navigating to (or lazily
 * loading) anything afterward tries to fetch a chunk hash that no longer
 * exists on the server, throwing ChunkLoadError. Suspense does not catch
 * render errors, so without this boundary that error would otherwise crash
 * the whole app.
 *
 * Reloads once automatically to pick up the new bundle. A sessionStorage flag
 * prevents a reload loop if the chunk is genuinely broken rather than stale.
 */
const RELOAD_FLAG = 'tf-chunk-reload-attempted';

function isChunkLoadError(error) {
  const message = String(error?.message ?? '');
  return (
    error?.name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message)
  );
}

class ChunkErrorBoundary extends React.Component {
  state = { hasNonChunkError: false };

  static getDerivedStateFromError(error) {
    if (isChunkLoadError(error)) {
      if (!sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, '1');
        window.location.reload();
        return {};
      }
      // Already tried reloading once this session and it still fails — show
      // the normal error state instead of reloading forever.
      return { hasNonChunkError: true };
    }
    return { hasNonChunkError: true };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) {
      // eslint-disable-next-line no-console
      console.error('Unhandled render error:', error);
    }
  }

  render() {
    if (this.state.hasNonChunkError) {
      return (
        <div style={{ padding: '3rem 1.5rem', textAlign: 'center' }}>
          <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
            Something went wrong loading this page.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.6rem 1.2rem',
              borderRadius: '0.5rem',
              border: 'none',
              background: '#2563eb',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
